import { notifyError } from '@/bugsnag';
import { csrfToken } from '@/components/Utils/Helper';
import type { BeforeFetchContext, UseFetchOptions } from '@vueuse/core';
import { createFetch, useFetch } from '@vueuse/core';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { computed } from 'vue';
import { fetch } from './fetch';

type BeforeFetchReturns = ReturnType<Exclude<UseFetchOptions['beforeFetch'], undefined>>; // the defined return types of `UseFetchOptions['beforeFetch']`
type JWT = string;
export type Portal = 'company' | 'contractor' | 'client' | 'employee';

type BuildFetchOpts = {
    auth?: 'jwt';
    baseUrl?: string;
    headers?: HeadersInit;
    onBeforeFetch?: (ctx: BeforeFetchContext, portal: Portal) => BeforeFetchReturns;
};
interface JwtTokenResponse {
    token: JWT;
}

const IS_TEST_RUN = import.meta.env.VITEST === 'true';

const defaultHeaders: HeadersInit = {
    Accept: 'application/json',
    'X-CSRF-TOKEN': csrfToken?.value,
} as const;

const EXPIRED_JWT = 'e30.eyJleHAiOjB9';

const jwtEndpoints: Record<Portal, string> = {
    client: '/jwt/client',
    company: '/jwt/user',
    contractor: '/jwt/contractor',
    employee: '/jwt/employee',
};

const requestRequiresJwt = (authOption: BuildFetchOpts['auth']) => authOption === 'jwt' && !IS_TEST_RUN;

const applyAuthHeaders = (headers: RequestInit['headers'], jwtToken: JwtTokenResponse['token'] | null) => ({
    ...headers,
    Authorization: `Bearer ${jwtToken}`,
});

// token exp is seconds, Date.now() is miliseconds
const isTokenExpiredNow = (jwtToken: JwtPayload | undefined) => (jwtToken?.exp ?? 0) * 1000 < Date.now();

export const buildJwtGetter = (portal: Portal) => {
    const {
        data: jwtResponse,
        error,
        execute,
        onFetchError,
        isFetching,
    } = useFetch(jwtEndpoints[portal], { headers: defaultHeaders }, { immediate: false }).json<JwtTokenResponse>();

    onFetchError(() =>
        notifyError(error.value, `api-client:refreshJwt:${portal}`, {
            portal,
            responseJson: { newJwt: jwtResponse.value },
            cause: `Error fetching JWT for ${portal}.`,
        }),
    );

    const jwtTokenPayload = computed<JwtPayload>(() => jwtDecode(jwtResponse.value?.token ?? EXPIRED_JWT));
    const isJwtStateInvalid = () => !!error.value || !jwtResponse.value?.token || isTokenExpiredNow(jwtTokenPayload.value);

    /**
     * This ensures that two refresh calls that happen near-simultaneously share the same promise
     * and api call result. Otherwise the second call to execute will abort the first
     */
    let inflightExecutionPromise: Promise<void> = Promise.resolve();
    const refreshJwtIfNeeded = (): Promise<void> | undefined => {
        if (isFetching.value) {
            return inflightExecutionPromise;
        }
        inflightExecutionPromise = Promise.resolve();
        if (isJwtStateInvalid()) {
            inflightExecutionPromise = execute();
            return inflightExecutionPromise;
        }
    };

    return {
        jwtString: computed(() => jwtResponse.value?.token ?? null),
        isJwtStateInvalid,
        refreshJwtIfNeeded,
    };
};

export const buildFetch = (portal: Portal) => {
    const { jwtString, isJwtStateInvalid, refreshJwtIfNeeded } = buildJwtGetter(portal);

    const buildBeforeFetch = (opts: BuildFetchOpts) => async (ctx: BeforeFetchContext) => {
        if (requestRequiresJwt(opts.auth)) {
            await refreshJwtIfNeeded();
            // renewal failed, abort main request
            if (isJwtStateInvalid()) {
                ctx.cancel();
                return;
            }
            ctx.options.headers = applyAuthHeaders(ctx.options.headers, jwtString.value);
        }
        return opts.onBeforeFetch?.(ctx, portal) ?? ctx;
    };

    return (opts: BuildFetchOpts = {}) =>
        createFetch({
            baseUrl: opts.baseUrl,
            fetchOptions: {
                headers: {
                    ...defaultHeaders,
                    ...opts.headers,
                },
            },
            options: {
                fetch,
                beforeFetch: buildBeforeFetch(opts),
            },
        });
};

export const useEmployeeApi = buildFetch('employee');
export const useCompanyApi = buildFetch('company');
export const useClientApi = buildFetch('client');
